import React from 'react';
import { StarIcon } from 'lucide-react';
import { Avatar } from './Avatar';
import { avatarColors } from '../data/coaches';
import type { CoachProfile, Testimonial } from '../data/testimonials';
type TestimonialsProps = {
  testimonials: Testimonial[];
  profiles?: CoachProfile[];
  title?: string;
  subtitle?: string;
  background?: 'dark' | 'mid';
};

/** Real reviews only. Renders nothing while the list is empty. */
export function Testimonials({
  testimonials,
  profiles = [],
  title = 'What coaches are saying',
  subtitle = 'From volunteer, parent and club coaches running junior squads.',
  background = 'dark'
}: TestimonialsProps) {
  if (testimonials.length === 0) return null;
  const bg = background === 'dark' ? 'bg-field' : 'bg-field-mid';
  return <section aria-label={title} className={`w-full text-cream ${bg}`} data-id="element-352" data-mp="2s42yxp0nvezs">
      <div className="mx-auto max-w-6xl px-5 py-16 sm:py-20" data-id="element-353" data-mp="v8wuwec8fset">
        <div className="text-center" data-id="element-354" data-mp="qa3pzab0h9q9">
          <p className="text-[11px] font-semibold uppercase tracking-[1.6px] text-willow" data-id="element-355" data-mp="qb51tgzuq091">Coach to coach</p>
          <h2 className="mt-3 font-display text-3xl text-cream sm:text-5xl" data-id="element-356" data-mp="h4n6gjawodem">{title}</h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm text-cream/70" data-id="element-357" data-mp="m6m5jgzm4o5r">{subtitle}</p>
        </div>

        {profiles.length > 0 && <div className="mt-9 grid gap-6 border-y border-field-line py-7 sm:grid-cols-2 sm:gap-10" data-id="element-358" data-mp="c6vddlyiyl8u">
            {profiles.map((profile, i) => <figure key={profile.name} className="flex gap-4" data-id="element-359" data-mp="zxr1a7zv0wat" data-mpi={i}>
                <Avatar name={profile.name} color={avatarColors[(i + 1) % avatarColors.length]} className="h-12 w-12 flex-none" data-id="element-360" data-mp="14v99fw61z7hr" />
                <div data-id="element-361" data-mp="q5v1msi4ky4h">
                  <figcaption data-id="element-362" data-mp="q6cvxjl8929r">
                    <span className="block font-display text-lg font-extrabold leading-tight text-cream" data-id="element-363" data-mp="v5prcsq5zhny">{profile.name}</span>
                    <span className="text-xs font-semibold uppercase tracking-wider text-willow" data-id="element-364" data-mp="1iv1xfjq739m1">{profile.role}</span>
                  </figcaption>
                  <blockquote className="mt-2 text-sm leading-relaxed text-cream/75" data-id="element-365" data-mp="1no3g82tivtaw">“{profile.bio}”</blockquote>
                </div>
              </figure>)}
          </div>}

        <div className={`mt-9 grid gap-4 sm:grid-cols-2 ${testimonials.length === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`} data-id="element-366" data-mp="1sjpgz9ibi3l6">
          {testimonials.map((item, i) => <figure key={item.author} className="flex flex-col rounded-2xl border border-field-line bg-field-card p-5 shadow-card" data-id="element-367" data-mp="1jhg3qbvnzw43" data-mpi={i}>
              <div className="flex items-center gap-2.5" data-id="element-368" data-mp="1el7vlifvx36f">
                <Avatar name={item.author} src={item.avatar} color={avatarColors[i % avatarColors.length]} className="h-11 w-11" data-id="element-369" data-mp="19rdbwf0a4o7c" />
                <span className="flex gap-0.5" role="img" aria-label="Five out of five" data-id="element-370" data-mp="14tle54cye7v8">
                  {[0, 1, 2, 3, 4].map(s => <StarIcon key={s} className="h-3.5 w-3.5 fill-willow text-willow" aria-hidden="true" data-id="element-371" data-mp="vsdmv68uu7an" />)}
                </span>
              </div>
              <blockquote className="mt-3 text-sm leading-relaxed text-cream/90" data-id="element-372" data-mp="qvn7kl4vjfqa">“{item.quote}”</blockquote>
              <figcaption className="mt-auto pt-4 text-xs text-cream/60" data-id="element-373" data-mp="32j9x8ilg7ehc">
                <span className="block text-[13px] font-semibold text-cream" data-id="element-374" data-mp="37e5tjuxpwo09">{item.author}</span>
                {item.role}
              </figcaption>
            </figure>)}
        </div>
      </div>
    </section>;
}